import { useMemo } from "react";
import fuzzysort from "fuzzysort";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { useListGoogleFonts } from "@/react/components/SettingsApp/Site/Pages/Edit/PageSettings/CustomFonts/GoogleFonts/hooks/useListGoogleFonts";
import { FONT_WEIGHT_OPTIONS } from "@/react/components/shared/BuilderUI/DefaultBlocks/Content/Button/Editor/constants";
import { useBuilderStore } from "@/react/components/shared/BuilderUI/Hooks/useBuilderStore";
import { FormTextSelect } from "@/react/components/shared/uikit/Form/FormTextSelect";
import { Loader } from "@/react/components/shared/uikit/Loader";
import { Form } from "@circle-react-shared/uikit/Form";
import { useIsCustomFontsEnabled } from "../../../Hooks/useIsCustomFontsEnabled";
import { FONT_FAMILY_OPTIONS } from "./constants";

interface FontOption {
  group: string;
  label: string;
  value: string;
}

const INITIAL_FONT_COUNT = 250;

export const FontPicker = ({ name }: { name: string }) => {
  const { data: googleFonts, isLoading: isLoadingGoogleFonts } =
    useListGoogleFonts();
  const isCustomFontsEnabled = useIsCustomFontsEnabled();
  const { setValue, watch } = useFormContext();
  const registerGoogleFontWeight = useBuilderStore(
    state => state.registerGoogleFontWeight,
  );

  const transformedGoogleFonts = useMemo(
    (): FontOption[] =>
      googleFonts?.fonts.slice(0, INITIAL_FONT_COUNT).map(font => ({
        group: "google",
        label: font.family,
        value: font.family,
      })) || [],
    [googleFonts],
  );

  const selectedFont = watch(name) as string;

  const allFontOptions = useMemo((): FontOption[] => {
    const baseOptions = [...transformedGoogleFonts, ...FONT_FAMILY_OPTIONS];

    // If the selected font is a Google font beyond the first 250, add it to options
    if (
      selectedFont &&
      !baseOptions.some(option => option.value === selectedFont)
    ) {
      const selectedGoogleFont = googleFonts?.fonts.find(
        font => font.family === selectedFont,
      );
      if (selectedGoogleFont) {
        return [
          ...baseOptions,
          {
            group: "google",
            label: selectedGoogleFont.family,
            value: selectedGoogleFont.family,
          },
        ];
      }
    }

    return baseOptions;
  }, [transformedGoogleFonts, selectedFont, googleFonts]);

  const customFilterBy = (
    options: FontOption[] | readonly FontOption[],
    search: string,
  ) => {
    if (!search.trim()) {
      return [...options];
    }

    const allGoogleFonts =
      googleFonts?.fonts.map(font => ({
        group: "google",
        label: font.family,
        value: font.family,
      })) || [];

    const allSearchableFonts = [...allGoogleFonts, ...FONT_FAMILY_OPTIONS];

    const searchResults = fuzzysort.go(search, allSearchableFonts, {
      key: "label",
      limit: 100,
      threshold: -10000,
    });

    return searchResults.map(result => result.obj);
  };

  const groupByFontFamily = useMemo(
    () => (option: FontOption) => option.group,
    [],
  );

  const handleFontChange = (selectedFont: string) => {
    const fontWeightFieldName = name.replace("fontFamily", "fontWeight");

    const isWebFont = FONT_FAMILY_OPTIONS.some(
      font => font.value === selectedFont,
    );

    let firstWeight: string;

    if (isWebFont) {
      firstWeight = FONT_WEIGHT_OPTIONS[0].value;
    } else {
      const selectedGoogleFont = googleFonts?.fonts.find(
        font => font.family === selectedFont,
      );

      if (selectedGoogleFont && selectedGoogleFont.variants.length > 0) {
        firstWeight = selectedGoogleFont.variants[0];

        registerGoogleFontWeight({
          family: selectedFont,
          weight: firstWeight,
        });
      } else {
        firstWeight = FONT_WEIGHT_OPTIONS[0].value;
      }
    }

    setValue(fontWeightFieldName, firstWeight);
  };

  if (!isCustomFontsEnabled) {
    return null;
  }

  if (isLoadingGoogleFonts) {
    return <Loader />;
  }

  return (
    <Form.Item
      isFirstChild
      hideBorder
      hideDescription
      name={name}
      labelClassName="!text-dark !text-xs !font-normal"
      label={t("builder_ui.css_variables.typography.variables.font_family")}
    >
      <FormTextSelect
        name={name}
        options={allFontOptions}
        groupBy={groupByFontFamily}
        filterBy={customFilterBy}
        onChange={handleFontChange}
      />
    </Form.Item>
  );
};
